import React, { useState, useEffect } from 'react';
import { adminAPI, useAdminAuth } from '@/contexts/AdminAuthContext';
import { auth } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useModal } from '@/contexts/ModalContext';
import { toast } from '@/components/ui/sonner';
import { 
  BookOpen, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Calendar,
  User,
  Tag,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Save,
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

interface Blog {
  _id: string;
  title: string;
  content: string;
  excerpt: string;
  author: {
    name: string;
    email: string;
  };
  category: string;
  tags: string[];
  status: 'draft' | 'published' | 'archived';
  featured: boolean;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  views: number;
  likes: number;
  createdBy?: string;
  imageUrl?: string;
  cloudinaryId?: string;
}

interface BlogsResponse {
  blogs: Blog[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalBlogs: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const AdminBlogsPage: React.FC = () => {
  const { } = useAdminAuth(); // Keep for auth context but don't use user object
  const { showConfirmModal } = useModal();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    page: 1,
    limit: 10
  });

  // Create/Edit blog modal
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    tags: '',
    status: 'published' as 'draft' | 'published' | 'archived',
    featured: false,
    createdBy: '',
    imageUrl: '',
    cloudinaryId: ''
  });

  // Image upload state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadBlogs();
  }, [filters]);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getBlogs(filters);
      setBlogs(response.data.blogs);
      setPagination(response.data.pagination);
    } catch (error: any) {
      setError(error.message || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const openCreateModal = () => {
    setEditingBlog(null);
    setBlogForm({
      title: '',
      content: '',
      excerpt: '',
      category: '',
      tags: '',
      status: 'published',
      featured: false,
      createdBy: '',
      imageUrl: '',
      cloudinaryId: ''
    });
    setImageFile(null);
    setImagePreview('');
    setShowBlogModal(true);
  };

  const openEditModal = (blog: Blog) => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title,
      content: blog.content,
      excerpt: blog.excerpt,
      category: blog.category,
      tags: blog.tags.join(', '),
      status: blog.status,
      featured: blog.featured,
      createdBy: blog.createdBy || blog.author?.name || 'Admin User',
      imageUrl: blog.imageUrl || '',
      cloudinaryId: blog.cloudinaryId || ''
    });
    setImageFile(null);
    setImagePreview(blog.imageUrl || '');
    setShowBlogModal(true);
  };

  // Handle image file selection
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload image to Cloudinary
  const uploadImage = async (): Promise<{ imageUrl: string; cloudinaryId: string } | null> => {
    if (!imageFile) return null;

    try {
      setUploadingImage(true);
      setError('');

      console.log('Starting image upload...', imageFile.name);

      const formData = new FormData();
      formData.append('image', imageFile);

      // Get the token from Firebase auth
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Not authenticated');
      }

      const token = await firebaseUser.getIdToken();
      console.log('Got auth token:', !!token);

      const response = await fetch('http://localhost:5000/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      console.log('Upload response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Upload error response:', errorData);
        throw new Error(errorData.message || 'Failed to upload image');
      }

      const result = await response.json();
      console.log('Upload success:', result);

      setSuccess('Image uploaded successfully!');

      return {
        imageUrl: result.data.imageUrl,
        cloudinaryId: result.data.cloudinaryId
      };
    } catch (error: any) {
      console.error('Image upload error:', error);
      setError(`Image upload failed: ${error.message}`);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveBlog = async () => {
    try {
      setActionLoading('save');
      setError('');
      setSuccess('');

      console.log('Starting blog save process...');
      console.log('Current blog form:', blogForm);
      console.log('Image file selected:', !!imageFile, imageFile?.name);

      // Upload image if a new one is selected
      let imageData = {
        imageUrl: blogForm.imageUrl,
        cloudinaryId: blogForm.cloudinaryId
      };

      if (imageFile) {
        console.log('Uploading new image...');
        const uploadResult = await uploadImage();
        if (uploadResult) {
          imageData = uploadResult;
          console.log('Image upload successful:', imageData);
        } else {
          console.error('Image upload failed');
          setError('Failed to upload image');
          return;
        }
      }

      const blogData = {
        ...blogForm,
        ...imageData,
        tags: blogForm.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      };

      console.log('Final blog data:', blogData);

      if (editingBlog) {
        console.log('Updating existing blog:', editingBlog._id);
        await adminAPI.updateBlog(editingBlog._id, blogData);
        setSuccess('Blog updated successfully');
      } else {
        console.log('Creating new blog...');
        await adminAPI.createBlog(blogData);
        setSuccess('Blog created successfully');
      }

      setShowBlogModal(false);
      await loadBlogs();
    } catch (error: any) {
      console.error('Blog save error:', error);
      setError(error.message || 'Failed to save blog');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteBlog = async (blogId: string) => {
    const confirmed = await showConfirmModal({
      title: 'Delete Blog',
      description: 'Are you sure you want to delete this blog? This action cannot be undone.',
      variant: 'destructive',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;

    try {
      setActionLoading(`delete-${blogId}`);
      setError('');
      setSuccess('');

      await adminAPI.deleteBlog(blogId);
      toast.success('Blog deleted successfully');
      await loadBlogs();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to delete blog';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatusChange = async (blogId: string, status: string) => {
    try {
      setActionLoading(`status-${blogId}`);
      setError('');
      setSuccess('');
      
      await adminAPI.updateBlogStatus(blogId, status);
      setSuccess(`Blog ${status} successfully`);
      await loadBlogs();
    } catch (error: any) {
      setError(error.message || 'Failed to update blog status');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'draft': { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      'published': { color: 'bg-green-100 text-green-800', label: 'Published' },
      'archived': { color: 'bg-yellow-100 text-yellow-800', label: 'Archived' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Blog Management</h1>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Blog Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Create, edit, and manage blog posts
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={loadBlogs}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={openCreateModal}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Blog
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <Alert className="bg-destructive/10 border-destructive/20">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-destructive">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-800">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Search
              </label>
              <Input
                placeholder="Title, content..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Categories</option>
                <option value="technology">Technology</option>
                <option value="ai">AI & Machine Learning</option>
                <option value="automation">Automation</option>
                <option value="tutorials">Tutorials</option>
                <option value="news">News</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={() => setFilters(prev => ({ ...prev, page: 1 }))}
                className="w-full"
                variant="outline"
              >
                <Search className="h-4 w-4 mr-2" />
                Apply
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Blogs List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Blog Posts ({pagination?.totalBlogs || 0})</span>
            <div className="text-sm text-muted-foreground">
              Page {pagination?.currentPage || 1} of {pagination?.totalPages || 1}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {blogs.map((blog) => (
              <div key={blog._id} className="border border-border rounded-lg p-4 hover:bg-accent/5 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-foreground">{blog.title}</h3>
                      {blog.featured && (
                        <Badge className="bg-accent/20 text-accent">Featured</Badge>
                      )}
                      {getStatusBadge(blog.status)}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {blog.excerpt}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span className="flex items-center">
                        <User className="h-3 w-3 mr-1" />
                        {blog.author.name}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(blog.createdAt)}
                      </span>
                      <span className="flex items-center">
                        <Tag className="h-3 w-3 mr-1" />
                        {blog.category}
                      </span>
                      <span className="flex items-center">
                        <Eye className="h-3 w-3 mr-1" />
                        {blog.views} views
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <select
                      value={blog.status}
                      onChange={(e) => handleStatusChange(blog._id, e.target.value)}
                      disabled={actionLoading === `status-${blog._id}`}
                      className="text-xs px-2 py-1 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(blog)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteBlog(blog._id)}
                      disabled={actionLoading === `delete-${blog._id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            
            {blogs.length === 0 && !loading && (
              <div className="text-center py-8 text-muted-foreground">
                No blog posts found matching your criteria
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((pagination.currentPage - 1) * filters.limit) + 1} to{' '}
            {Math.min(pagination.currentPage * filters.limit, pagination.totalBlogs)} of{' '}
            {pagination.totalBlogs} blogs
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            
            <span className="text-sm text-muted-foreground">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create/Edit Blog Modal */}
      {showBlogModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-foreground">
                {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBlogModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    Title *
                  </label>
                  <Input
                    value={blogForm.title}
                    onChange={(e) => setBlogForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter blog title"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    Category *
                  </label>
                  <select
                    value={blogForm.category}
                    onChange={(e) => setBlogForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select category</option>
                    <option value="technology">Technology</option>
                    <option value="ai">AI & Machine Learning</option>
                    <option value="automation">Automation</option>
                    <option value="tutorials">Tutorials</option>
                    <option value="news">News</option>
                  </select>
                </div>
              </div>

              {/* Creator Name Field */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">
                  Creator's Name *
                </label>
                <Input
                  value={blogForm.createdBy}
                  onChange={(e) => setBlogForm(prev => ({ ...prev, createdBy: e.target.value }))}
                  placeholder="Enter creator's name"
                />
              </div>

              {/* Image Upload Section */}
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">
                  Blog Image
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="image-upload"
                      disabled={uploadingImage}
                    />
                    <label
                      htmlFor="image-upload"
                      className={`flex items-center space-x-2 px-4 py-2 border border-border rounded-md cursor-pointer hover:bg-accent/5 ${
                        uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {uploadingImage ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      <span>{uploadingImage ? 'Uploading...' : 'Choose Image'}</span>
                    </label>
                    {imageFile && !uploadingImage && (
                      <span className="text-sm text-muted-foreground">
                        {imageFile.name}
                      </span>
                    )}
                    {uploadingImage && (
                      <span className="text-sm text-blue-600">
                        Uploading to Cloudinary...
                      </span>
                    )}
                  </div>

                  {imagePreview && (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full max-w-md h-48 object-cover rounded-md border border-border"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview('');
                          setBlogForm(prev => ({ ...prev, imageUrl: '', cloudinaryId: '' }));
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">
                  Excerpt *
                </label>
                <Textarea
                  value={blogForm.excerpt}
                  onChange={(e) => setBlogForm(prev => ({ ...prev, excerpt: e.target.value }))}
                  placeholder="Brief description of the blog post"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">
                  Content *
                </label>
                <Textarea
                  value={blogForm.content}
                  onChange={(e) => setBlogForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Write your blog content here..."
                  rows={10}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    Tags
                  </label>
                  <Input
                    value={blogForm.tags}
                    onChange={(e) => setBlogForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="tag1, tag2, tag3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    Status
                  </label>
                  <select
                    value={blogForm.status}
                    onChange={(e) => setBlogForm(prev => ({ ...prev, status: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={blogForm.featured}
                    onChange={(e) => setBlogForm(prev => ({ ...prev, featured: e.target.checked }))}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <label htmlFor="featured" className="text-sm font-medium text-foreground">
                    Featured Post
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-2 p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setShowBlogModal(false)}
              >
                Cancel
              </Button>

              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <div className="text-xs text-muted-foreground mr-2">
                  Debug: {JSON.stringify({
                    title: !!blogForm.title.trim(),
                    content: !!blogForm.content.trim(),
                    category: !!blogForm.category,
                    createdBy: !!blogForm.createdBy.trim(),
                    loading: actionLoading === 'save',
                    uploading: uploadingImage
                  })}
                </div>
              )}

              <Button
                onClick={handleSaveBlog}
                disabled={
                  actionLoading === 'save' ||
                  uploadingImage ||
                  !blogForm.title.trim() ||
                  !blogForm.content.trim() ||
                  !blogForm.category ||
                  !blogForm.createdBy.trim()
                }
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 transition-colors duration-200"
              >
                {actionLoading === 'save' || uploadingImage ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {uploadingImage ? 'Uploading Image...' : actionLoading === 'save' ? 'Saving...' : editingBlog ? 'Update Blog' : 'Create Blog'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogsPage;
